import { getCookie, setCookie, deleteCookie } from "cookies-next";
import { cookieOptions } from "@/utils/cookie";

/**
 * 跨站（cn ↔ gl）登录态交接。
 *
 * 令牌不再放 URL 查询参数（安全审计 VULN-031：会进 nginx 访问日志与浏览器历史，
 * 落地页无条件采信还能被构造链接做会话固定）。按两站域名关系分两条路：
 * - mall.x ↔ global.x 成对域名：写父域名下 60 秒的交接 cookie，落地读完即删，URL 里没有令牌；
 * - 裸 IP / 不同父域名（DetectionIp.getRedirectUrl 走兜底域名）：浏览器不允许跨域写 cookie，
 *   改放 URL 的 #（不发往服务端、不进 Referer），落地只认来自我们自己站点的跳转。
 */
const TOKEN_KEY = "xsite_token";
const USER_KEY = "xsite_user";
const MAX_AGE = 60;

const hostOf = (url?: string) => {
  try {
    return new URL(url || "").hostname.toLowerCase();
  } catch {
    return "";
  }
};
// 两站域名首段（如 mall / global）：prd 多套品牌域名都是这两个前缀成对出现
const SITE_PREFIXES = [process.env.CN_SITE_HOST, process.env.GL_SITE_HOST].map((url) => hostOf(url).split(".")[0]).filter(Boolean);

/**
 * # 令牌只采信来自我们自己站点的跳转：任意裸 IP 入口（不固定，没法列清单）、
 * 或首段是两站前缀的域名。邮件 / 聊天里点开的构造链接没有这类 referrer，会被挡掉
 */
function isTrustedReferrer(): boolean {
  const host = hostOf(document.referrer);
  if (!host) return false;
  return isIpHost(host) || SITE_PREFIXES.includes(host.split(".")[0]);
}

/** 判断是否为裸 IP（IPv4 / IPv6，纯字符串处理，不用正则） */
export function isIpHost(host: string): boolean {
  // IPv6：含冒号或带方括号
  if (host.startsWith("[") || host.includes(":")) return true;
  // IPv4：四段且每段均为 1~3 位数字
  const seg = host.split(".");
  const isNumeric = (s: string) => {
    if (s.length < 1 || s.length > 3) return false;
    for (let i = 0; i < s.length; i++) {
      const code = s.charCodeAt(i);
      if (code < 48 || code > 57) return false; // 非 0-9
    }
    return true;
  };
  return seg.length === 4 && seg.every(isNumeric);
}

/** 两个域名共有的父域名（至少两段，如 dooprimerewards.com）；有一方是 IP 或没有共同部分时返回 "" */
export function sharedParentDomain(a: string, b: string): string {
  if (isIpHost(a) || isIpHost(b)) return "";
  const x = a.toLowerCase().split(".").reverse();
  const y = b.toLowerCase().split(".").reverse();
  const common: string[] = [];
  for (let i = 0; i < Math.min(x.length, y.length) && x[i] === y[i]; i++) common.unshift(x[i]);
  return common.length >= 2 ? common.join(".") : "";
}

/**
 * 发起方：把登录态交给目标站。
 * @returns 要拼在目标 URL 末尾的 hash；cookie 交接成功时为空串
 */
export function handOffSession(targetOrigin: string, token: string, user: string): string {
  const domain = sharedParentDomain(window.location.hostname, new URL(targetOrigin).hostname);
  if (domain) {
    const opts = { ...cookieOptions(), domain, maxAge: MAX_AGE };
    setCookie(TOKEN_KEY, token, opts);
    setCookie(USER_KEY, user, opts);
    // 浏览器会静默拒绝不合法的 domain（如公共后缀），必须读回确认
    if (getCookie(TOKEN_KEY) === token) return "";
  }
  return `#${new URLSearchParams({ udid: token, user }).toString()}`;
}

/** 删掉交接 cookie：写入时的 domain 是哪一级父域名不确定，当前域名往上逐级都删一遍 */
function clearHandOffCookies() {
  const labels = window.location.hostname.split(".");
  for (let i = 0; i <= labels.length - 2; i++) {
    const domain = labels.slice(i).join(".");
    deleteCookie(TOKEN_KEY, { path: "/", domain });
    deleteCookie(USER_KEY, { path: "/", domain });
  }
  deleteCookie(TOKEN_KEY, { path: "/" });
  deleteCookie(USER_KEY, { path: "/" });
}

/**
 * 接收方：取出交接过来的登录态，取后即删。
 * @returns 没有交接、或 # 令牌来源不可信时返回 null
 */
export function takeHandedOffSession(): { token: string; user: string } | null {
  const cookieToken = getCookie(TOKEN_KEY);
  if (cookieToken) {
    const user = getCookie(USER_KEY);
    clearHandOffCookies();
    return { token: String(cookieToken), user: user ? String(user) : "" };
  }

  const hash = new URLSearchParams(window.location.hash.slice(1));
  const udid = hash.get("udid");
  if (!udid) return null;
  // 不管采不采信，先从地址栏清掉；保留 history.state，否则 Next router 后退会出错
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  if (!isTrustedReferrer()) return null;
  return { token: udid, user: hash.get("user") || "" };
}

// 应用启动时接到的交接，留给 RedirectSignIn 取用
let adopted: { token: string; user: string } | null = null;
// 本次页面加载是否带着 _fromGeo 落地：免登收尾会把 _fromGeo 从 router.query 清掉，DetectionIp 靠它防两站来回弹
let landedFromCrossSite = false;

/** 本次页面加载是跨站跳过来的（带 _fromGeo=1 落地） */
export function isLandedFromCrossSite() {
  return landedFromCrossSite;
}

/**
 * 应用启动（_app 模块加载，早于任何页面组件与请求）时就接住交接并写入 token / user cookie。
 * 等 RedirectSignIn 的 effect 再写就晚了：落地页（如 /user/voucher）一挂载就发的请求不带 token，
 * 401 先回来会被当成未登录跳 /login，后回来会清掉刚写好的登录态。
 */
export function adoptHandOffEarly() {
  if (typeof window === "undefined") return;
  landedFromCrossSite = new URLSearchParams(window.location.search).get("_fromGeo") === "1";
  adopted = takeHandedOffSession();
  if (adopted) {
    setCookie("token", adopted.token, cookieOptions());
    setCookie("user", adopted.user, cookieOptions());
  }
}

/** RedirectSignIn 取启动时接到的交接（只给一次）；没有则当场再取，覆盖 SPA 内路由到 /login 等情况 */
export function consumeHandOff(): { token: string; user: string } | null {
  const early = adopted;
  adopted = null;
  return early || takeHandedOffSession();
}
